import React, { useEffect, useRef, useState } from "react";
import { AVATAR_VIDEO_ID } from "./AuraProvider";

// Simmi as a cut-out person, like Docket's avatar: no box, no studio backdrop. Each frame of her video (the
// idle loop, or the live Anam stream while she talks) goes through Google's MediaPipe person segmentation in
// the browser, and only the person is drawn. If segmentation can't load, the video is shown with soft faded
// edges instead.

const MEDIAPIPE_VERSION = "1.0.1"; // keep in step with @mediapipe/tasks-vision in package.json
const WASM_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/wasm`;
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite";
const WORK_WIDTH = 256; // segment at a small size (fast); the mask is scaled up with smoothing

let segmenterPromise = null; // one segmenter shared by every avatar on the page
function getSegmenter() {
  if (!segmenterPromise) {
    segmenterPromise = (async () => {
      const { FilesetResolver, ImageSegmenter } = await import("@mediapipe/tasks-vision");
      const fileset = await FilesetResolver.forVisionTasks(WASM_URL);
      const options = (delegate) => ({
        baseOptions: { modelAssetPath: MODEL_URL, delegate },
        runningMode: "VIDEO",
        outputConfidenceMasks: true,
        outputCategoryMask: false,
      });
      try {
        return await ImageSegmenter.createFromOptions(fileset, options("GPU")); // fastest where WebGL is available
      } catch {
        return ImageSegmenter.createFromOptions(fileset, options("CPU"));
      }
    })().catch((err) => {
      segmenterPromise = null;
      throw err;
    });
  }
  return segmenterPromise;
}

// Sharpen the model's soft probability into a clean edge with a little feathering.
const edge = (p) => {
  const t = Math.min(1, Math.max(0, (p - 0.35) / 0.3));
  return t * t * (3 - 2 * t);
};

// Hover sheen (before a conversation starts): a soft white band sweeps diagonally across her, drawn only on
// her silhouette.
const SHEEN_MS = 1800;
const SWAP_FADE_MS = 450; // when her live stream takes over from the idle clip, it fades in instead of cutting
const drawSheen = (ctx, W, H, t) => {
  const reach = W + H; // the band travels from above her top-left corner to past her bottom-right
  const pos = -0.25 * reach + t * 1.5 * reach;
  const band = 0.22 * reach;
  const g = ctx.createLinearGradient(pos - band, pos - band, pos + band, pos + band);
  g.addColorStop(0, "rgba(255,255,255,0)");
  g.addColorStop(0.5, "rgba(255,255,255,0.38)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.globalCompositeOperation = "source-atop";
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
};

const SegmentedAvatar = ({ idleSrcs, liveActive, liveVisible, muted, sheen = false, className = "" }) => {
  const sheenStartRef = useRef(null);
  useEffect(() => {
    if (sheen) sheenStartRef.current = performance.now(); // each hover plays the sweep once
  }, [sheen]);
  const idleRef = useRef(null);
  const liveRef = useRef(null);
  const canvasRef = useRef(null);
  const liveRefFlags = useRef({ liveActive, liveVisible });
  liveRefFlags.current = { liveActive, liveVisible };
  const [state, setState] = useState("loading"); // loading | ready | fallback
  const [portrait, setPortrait] = useState(false); // tall clips are framed differently from Anam's wide video
  // Before the conversation: the idle clips play one after another (a single clip just loops).
  const [clip, setClip] = useState(0);
  const idleSrc = idleSrcs[clip % idleSrcs.length];
  const cycling = idleSrcs.length > 1;

  useEffect(() => {
    let segmenter = null;
    let cancelled = false;
    getSegmenter()
      .then((s) => {
        if (!cancelled) segmenter = s;
      })
      .catch((err) => {
        console.warn("Simmi: background removal unavailable, using soft edges", err); // eslint-disable-line no-console
        if (!cancelled) setState("fallback");
      });

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const work = document.createElement("canvas");
    const workCtx = work.getContext("2d", { willReadFrequently: true });
    const maskCanvas = document.createElement("canvas");
    const maskCtx = maskCanvas.getContext("2d");
    let maskImage = null;
    let hasMask = false;
    let frame;
    let lastSrc = null;
    let tick = 0; // the person mask is recomputed every other frame (plenty for a talking head, half the CPU)
    let swapStart = 0;

    const draw = () => {
      frame = requestAnimationFrame(draw);
      const { liveActive: la, liveVisible: lv } = liveRefFlags.current;
      const live = liveRef.current;
      const src = la && lv && live && live.readyState >= 2 && live.videoWidth ? live : idleRef.current;
      if (!src || src.readyState < 2 || !src.videoWidth) return;

      const W = src.videoWidth;
      const H = src.videoHeight;
      if (src !== lastSrc) {
        if (lastSrc) swapStart = performance.now();
        lastSrc = src;
        setPortrait(H > W);
      }
      if (canvas.width !== W || canvas.height !== H) {
        canvas.width = W;
        canvas.height = H;
      }
      const ww = WORK_WIDTH;
      const wh = Math.round((H / W) * WORK_WIDTH);
      if (work.width !== ww || work.height !== wh) {
        work.width = maskCanvas.width = ww;
        work.height = maskCanvas.height = wh;
        maskImage = maskCtx.createImageData(ww, wh);
        hasMask = false;
      }

      // Person mask for this frame.
      tick += 1;
      if (segmenter && (tick % 2 === 0 || !hasMask)) {
        try {
          workCtx.drawImage(src, 0, 0, ww, wh);
          const result = segmenter.segmentForVideo(work, performance.now());
          const mask = result.confidenceMasks && result.confidenceMasks[0];
          if (mask) {
            const probs = mask.getAsFloat32Array();
            const px = maskImage.data;
            for (let i = 0; i < probs.length; i += 1) {
              const o = i * 4;
              px[o] = 255;
              px[o + 1] = 255;
              px[o + 2] = 255;
              px[o + 3] = edge(probs[i]) * 255;
            }
            maskCtx.putImageData(maskImage, 0, 0);
            hasMask = true;
          }
          result.close();
        } catch {
          /* a dropped frame keeps the previous mask */
        }
      }

      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, W, H);
      ctx.globalAlpha = swapStart ? Math.min(1, (performance.now() - swapStart) / SWAP_FADE_MS) : 1;
      if (ctx.globalAlpha >= 1) swapStart = 0;
      ctx.drawImage(src, 0, 0, W, H);
      ctx.globalAlpha = 1;
      if (hasMask) {
        ctx.globalCompositeOperation = "destination-in"; // keep only the person
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(maskCanvas, 0, 0, W, H);
        setState((s) => (s === "ready" ? s : "ready"));
      }
      if (sheenStartRef.current !== null) {
        const t = (performance.now() - sheenStartRef.current) / SHEEN_MS;
        if (t >= 1) sheenStartRef.current = null;
        else drawSheen(ctx, W, H, t);
      }

      ctx.globalCompositeOperation = "source-over";
    };
    draw();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, []);

  // The speaker button mutes Simmi's voice (the live stream's audio plays through the hidden video).
  useEffect(() => {
    if (liveRef.current) liveRef.current.muted = muted;
  }, [muted, liveActive]);

  const fallback = state === "fallback";
  return (
    <>
      <video
        ref={idleRef}
        src={idleSrc}
        crossOrigin="anonymous"
        autoPlay
        muted
        loop={!cycling}
        onEnded={cycling ? () => setClip((i) => (i + 1) % idleSrcs.length) : undefined}
        playsInline
        className="aura-hidden-video"
      />
      {liveActive && (
        <video id={AVATAR_VIDEO_ID} ref={liveRef} autoPlay playsInline muted={muted} className="aura-hidden-video" />
      )}
      <canvas
        ref={canvasRef}
        aria-label="Simmi"
        role="img"
        className={`${fallback ? "aura-stage-media" : `aura-stage-cutout ${portrait ? "aura-stage-cutout--portrait" : ""}`} ${className} transition-opacity duration-500 ${
          state === "loading" ? "opacity-0" : "opacity-100"
        }`}
      />
    </>
  );
};

export default SegmentedAvatar;
